/**
 * paper-format.helper.ts
 * Tiện ích chuẩn hóa kích thước bao bì và khổ giấy nhà máy APV (Đơn vị duy nhất: mm).
 */

/**
 * Chuẩn hóa chuỗi khổ giấy (in, cắt, mua) về chuẩn duy nhất `[W]x[L]` (mm).
 * Hỗ trợ các định dạng nhập liệu tự do: 65x86, 65*86, 65 x 86, 65x 85, 650 x 860mm, 65*68...
 * Nếu kích thước < 120 (người dùng quen tay gõ cm), tự động nhân 10 thành mm (VD: 65x86 -> 650x860).
 */
export function normalizePaperDimension(input: string | null | undefined): string {
  if (!input || typeof input !== 'string') return '';
  const trimmed = input.trim();
  if (!trimmed) return '';

  // Tìm 2 cụm số ngăn cách bởi x, X, *, dấu cách hoặc dấu gạch
  // Cho phép số thập phân (vd: 65.5 x 86)
  const match = trimmed.match(/^(\d+(?:\.\d+)?)\s*(?:[xX*×\s/,-])\s*(\d+(?:\.\d+)?)(?:\s*(?:mm|cm|in|inch|tờ|sheet))?$/i);
  if (!match) {
    // Thử tìm bất kỳ 2 cụm số nào trong chuỗi
    const numbers = trimmed.match(/\d+(?:\.\d+)?/g);
    if (numbers && numbers.length >= 2) {
      let w = parseFloat(numbers[0]);
      let l = parseFloat(numbers[1]);
      if (isNaN(w) || isNaN(l)) return trimmed;
      // Nếu số < 120 -> người dùng gõ cm -> nhân 10 sang mm
      if (w > 0 && w < 120) w = Math.round(w * 10);
      if (l > 0 && l < 120) l = Math.round(l * 10);
      return `${w}x${l}`;
    }
    return trimmed;
  }

  let w = parseFloat(match[1]);
  let l = parseFloat(match[2]);

  if (isNaN(w) || isNaN(l)) return trimmed;

  // Nếu cả 2 kích thước đều < 120, quy đổi cm sang mm (nhân 10)
  if (w > 0 && w < 120) w = Math.round(w * 10);
  if (l > 0 && l < 120) l = Math.round(l * 10);

  return `${w}x${l}`;
}

/**
 * Định dạng hiển thị đẹp mắt cho người dùng xem: `650 × 860 mm`.
 */
export function formatPaperDimensionDisplay(dim: string | null | undefined): string {
  if (!dim || typeof dim !== 'string') return '';
  const normalized = normalizePaperDimension(dim);
  if (!normalized) return '';
  const parts = normalized.split('x');
  if (parts.length === 2) {
    return `${parts[0]} × ${parts[1]} mm`;
  }
  return dim;
}

/**
 * Kiểm tra xem kích thước sản phẩm (Dài, Rộng, Cao) có nhỏ bất thường không (< 10mm).
 * Hộp bao bì công nghiệp hầu như không có kích thước < 10mm (1cm), khả năng cao người dùng gõ nhầm cm.
 */
export function isSuspiciouslySmallDimension(val: number | string | null | undefined): boolean {
  if (val === null || val === undefined || val === '') return false;
  const num = typeof val === 'number' ? val : parseFloat(String(val));
  if (isNaN(num)) return false;
  return num > 0 && num < 10;
}
